import { useEffect } from 'react';
import { ProductType } from '@tink/core/src/types';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useProduct } from '../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { ProductDimensionAccountCheck } from '../../../models/product';
import { isMultibanking } from '../../../models/configuration';
import { useAppSelector } from '../../../stores/redux-store';
import { resetMultiBankingSession, useMultiBankingUser } from './multibankingUtils';

/**
 * useCheckMultibankingSession
 *
 * This hook is used to check if the current app configuration supports multibanking
 * and it also handles URL modification when the user session changes.
 *
 * @returns {boolean} - Returns true if the conditions for multibanking check are met, otherwise false.
 */

export const useCheckMultibankingSession = () => {
  const { data: config } = useConfiguration();
  const { productType, productDimension } = useProduct();

  // Retrieve the current user session from the Redux store
  const currentUserId = useAppSelector((state) => state.user.user?.id);
  // Retrieve the previous user session from the URL
  const previousUserId = useMultiBankingUser();

  /**
   * Check if both the current and previous user sessions exist and are different.
   * If so, remove the connected providers and multibanking user query parameters from the URL.
   * */
  useEffect(() => {
    if (currentUserId && previousUserId && currentUserId !== previousUserId) {
      resetMultiBankingSession();
    }
  }, [currentUserId, previousUserId]);

  return (
    isMultibanking(config) &&
    ProductType.INCOME_CHECK === productType &&
    ProductDimensionAccountCheck.CREATE_REPORT === productDimension
  );
};
